import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const SLUGS = [
  "groepenkasten",
  "verlichting",
  "beveiliging",
  "laadpalen",
  "thuisbatterij",
  "netwerken",
] as const;

export type FaqRow = {
  id: string;
  category_slug: string;
  question: string;
  answer: string;
  sort_order: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
};

export const listPublicFaqs = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({ slug: z.enum(SLUGS) }).parse(d),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data: rows, error } = await supabaseAdmin
      .from("category_faqs")
      .select("id, question, answer, sort_order")
      .eq("category_slug", data.slug)
      .eq("is_published", true)
      .order("sort_order", { ascending: true });
    if (error) throw new Error(error.message);
    return {
      faqs: (rows ?? []).map((r) => ({
        id: r.id,
        q: r.question,
        a: r.answer,
        sort_order: r.sort_order,
      })),
    };
  });

async function assertAdmin(ctx: { supabase: any; userId: string }) {
  const { data: row } = await ctx.supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", ctx.userId)
    .eq("role", "admin")
    .maybeSingle();
  if (!row) throw new Error("Niet geautoriseerd");
}


export const adminListFaqs = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data, error } = await supabaseAdmin
      .from("category_faqs")
      .select("*")
      .order("category_slug", { ascending: true })
      .order("sort_order", { ascending: true });
    if (error) throw new Error(error.message);
    return { faqs: (data ?? []) as FaqRow[] };
  });

const upsertSchema = z.object({
  id: z.string().uuid().optional(),
  category_slug: z.enum(SLUGS),
  question: z.string().min(1).max(500),
  answer: z.string().min(1).max(5000),
  sort_order: z.number().int().min(0).max(9999).default(0),
  is_published: z.boolean().default(true),
});

export const adminUpsertFaq = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => upsertSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    if (data.id) {
      const { error } = await supabaseAdmin
        .from("category_faqs")
        .update({
          category_slug: data.category_slug,
          question: data.question,
          answer: data.answer,
          sort_order: data.sort_order,
          is_published: data.is_published,
        })
        .eq("id", data.id);
      if (error) throw new Error(error.message);
    } else {
      const { error } = await supabaseAdmin
        .from("category_faqs")
        .insert({
          category_slug: data.category_slug,
          question: data.question,
          answer: data.answer,
          sort_order: data.sort_order,
          is_published: data.is_published,
        });
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

export const adminDeleteFaq = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ id: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { error } = await supabaseAdmin
      .from("category_faqs")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
